import { GridColDef, GridSortItem } from '@mui/x-data-grid-pro';
import { map, pick } from 'ramda';

import { DataGridConfig } from '../useDataGridConfig/useDataGridConfig.types';
import { GridDensityType } from '~/components/DataGridCustom';

import {
  UseDataGridRemoteStateState,
  DataGridConfigFormat,
  LegacyConfigFormat,
  UseDataGridRemoteStateUpdateReason,
} from './useDataGridRemoteState.types';

type StateFromConfig = (
  config: LegacyConfigFormat | DataGridConfigFormat,
  columns: GridColDef[],
) => DataGridConfigFormat;

function isLegacyFormat(config: LegacyConfigFormat | DataGridConfigFormat): config is LegacyConfigFormat {
  return Array.isArray(config);
}
export const stateFromConfig: StateFromConfig = (config: any, columns) => {
  if (isLegacyFormat(config)) {
    const columnMap = columns.reduce(
      (acc, opt) => {
        acc[opt.field] = opt;
        return acc;
      },
      {} as Record<string, GridColDef>,
    );
    const sort: GridSortItem[] = [];
    const columnVisibility: DataGridConfigFormat['columnVisibility'] = {};
    const columnWidth: DataGridConfigFormat['columnWidth'] = {};
    const columnOrder: DataGridConfigFormat['columnOrder'] = [];
    config.forEach((item) => {
      const column = columnMap[item.name];
      // check if column exists
      if (column) {
        if (item.hidden) {
          columnVisibility[item.name] = false;
        }
        if (item.width !== column.width) {
          columnWidth[item.name] = item.width;
        }
        if (item.sortBy) {
          sort.push({ field: item.name, sort: item.sortBy });
        }
        columnOrder.push(item.name);
      }
    });
    return {
      density: GridDensityType.STANDARD,
      sort,
      columnVisibility,
      columnWidth,
      columnOrder,
    };
  }
  const parsed = config as DataGridConfigFormat;
  if (parsed.sort) {
    const sortableFields = new Set(columns.filter((col) => col.sortable !== false).map((col) => col.field));
    return { ...parsed, sort: parsed.sort.filter((item) => sortableFields.has(item.field)) };
  }
  return parsed;
};

export const pickPersistableState = pick<(keyof DataGridConfigFormat)[]>([
  'columnOrder',
  'columnWidth',
  'columnVisibility',
  'sort',
  'density',
]);

export const dimensionsFromWidthMap: (m: Record<string, number>) => Record<string, { width: number }> = map(
  (width) => ({
    width,
  }),
) as any;

export const getInitialState = (config: DataGridConfig): UseDataGridRemoteStateState => ({
  density: config.density || GridDensityType.STANDARD,
  columnVisibility: config.columnVisibilityModel || {},
  sort: config.sortModel,
  columnWidth: undefined,
  columnOrder: undefined,
  updateReason: UseDataGridRemoteStateUpdateReason.INITIAL,
});

export const getDataGridState = (state: Pick<UseDataGridRemoteStateState, 'columnOrder' | 'columnWidth'>) => ({
  columns: {
    orderedFields: state.columnOrder,
    dimensions: state.columnWidth && dimensionsFromWidthMap(state.columnWidth),
  },
});
